// Turbo Morph Coordinator
//
// Coordinates periodic page refreshes with form interactions to prevent morphing
// from clobbering interactive content. Four mechanisms work together:
//
// 1. Periodic Refresh
//    Reads `turbo-refresh-interval` meta tag and calls turboRefresh() (lib/helpers) on that
//    interval. Re-initializes on each turbo:load (page navigation). A tick in a hidden tab
//    is skipped and remembered. When the tab is shown again, it refreshes at once and
//    restarts the interval. These timed refreshes stamp data-quiet-refresh on <html>, which
//    hides the Turbo progress bar (theme.css) until the next other visit or form submission.
//
// 2. Submission Guard
//    Blocks refreshes while a form submission is in-flight. Tracks submissions via a counter
//    that increments on turbo:submit-start and decrements on turbo:submit-end. When the last
//    submission completes, any blocked refresh is reattempted.
//
//    Manual incrementing/decrementing for non-turbo submissions (e.g. Stripe):
//      document.dispatchEvent(new CustomEvent("fsp:increment"))
//      document.dispatchEvent(new CustomEvent("fsp:decrement"))
//
// 3. Morph Lock
//    Preserves interactive content during morph refreshes. Only two element types are
//    checked — <form> and <turbo-frame> — everything else is always morphable.
//
//    Locking:
//
//    - <form> with a [name=commit] (f.submit) or [name=button] (f.button) element is
//      locked. This preserves user input in interactive forms during periodic refreshes.
//    - <turbo-frame> with a src attribute pointing to a different path than the current
//      page is locked. This auto-detects frames that have navigated away from their
//      default content (e.g. delete confirmations, edit forms). A frame with no src or
//      a src matching the current page is showing default content and can be safely
//      morphed.
//
//    Submission override:
//
//    When a form submits, turbo:submit-start stamps data-morphable on it. A locked
//    element (form or frame) that finds this attribute among its descendants allows the
//    morph through. This lets 422 validation errors and post-action redirects update
//    the DOM. The attribute is self-cleaning: the server response replaces the content,
//    which won't have it.
//
// 4. Same-Page Links
//    A link to the current page (e.g. the active sidebar item) is an advance visit, which
//    replaces the body and replays entrance animations. It is visited as a replace instead,
//    which Turbo treats as a page refresh and morphs.

import { turboRefresh } from "../lib/helpers"

// -- Shared helpers --

function isRefresh(url) {
  const visit = new URL(url)
  const current = new URL(window.location.href)
  return visit.origin + visit.pathname + visit.search ===
    current.origin + current.pathname + current.search
}

function hasNavigatedSrc(frame) {
  const src = frame.getAttribute("src")
  if (!src) return false
  const url = new URL(src, window.location.origin)
  return url.pathname + url.search !==
    window.location.pathname + window.location.search
}

// -- Periodic Refresh --

const INTERVAL_META_NAME = "turbo-refresh-interval"
let intervalId = null
let refreshMissed = false
let quietRefresh = false

function getRefreshInterval() {
  const meta = document.querySelector(`meta[name="${INTERVAL_META_NAME}"]`)
  if (!meta) return null
  const value = parseInt(meta.content, 10)
  return isNaN(value) || value <= 0 ? null : value
}

function setupPeriodicRefresh() {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
  refreshMissed = false

  const interval = getRefreshInterval()
  if (!interval) return
  intervalId = setInterval(periodicRefresh, interval * 1000)
}

function refreshQuietly() {
  quietRefresh = true
  turboRefresh()
}

function periodicRefresh() {
  if (document.hidden) {
    refreshMissed = true
  } else {
    refreshQuietly()
  }
}

document.addEventListener("turbo:load", setupPeriodicRefresh)

document.addEventListener("visibilitychange", () => {
  if (document.hidden || !refreshMissed) return
  setupPeriodicRefresh()
  refreshQuietly()
})

// Turbo starts the refresh visit a frame plus a 150ms debounce after turboRefresh().
document.addEventListener("turbo:visit", (event) => {
  const quiet = quietRefresh && isRefresh(event.detail.url)
  document.documentElement.toggleAttribute("data-quiet-refresh", quiet)
  quietRefresh = false
})

document.addEventListener("turbo:submit-start", () => {
  document.documentElement.removeAttribute("data-quiet-refresh")
})

// Frame-local settings edits (e.g. checkup frequency) update the body without a
// full visit. Keep the meta tag and timer in sync with the submitted value.
document.addEventListener("turbo:submit-end", (event) => {
  if (!event.detail.success) return
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return
  const input = form.elements.namedItem("application[checkup_frequency]")
  if (!input || typeof input.value !== "string") return
  const value = parseInt(input.value, 10)
  if (isNaN(value) || value <= 0) return
  const meta = document.querySelector(`meta[name="${INTERVAL_META_NAME}"]`)
  if (meta) meta.content = String(value)
  setupPeriodicRefresh()
})

// -- Same-Page Links --

document.addEventListener("turbo:click", (event) => {
  const link = event.target
  const url = new URL(event.detail.url)
  if (url.hash || !isRefresh(url.href)) return
  if (link.closest("[data-turbo-action], [data-turbo-frame], turbo-frame")) return

  // Cancelling turbo:click alone hands the click back to the browser (a full load).
  event.preventDefault()
  event.detail.originalEvent.preventDefault()
  window.Turbo.visit(url.href, { action: "replace" })
})

// -- Submission Guard --

let submissionCounter = 0
let refreshBlocked = false

function submissionStarted() {
  submissionCounter++
}

function submissionEnded() {
  submissionCounter = Math.max(0, submissionCounter - 1)

  if (submissionCounter === 0 && refreshBlocked) {
    refreshBlocked = false
    turboRefresh()
  }
}

function guardRefresh(event) {
  if (submissionCounter > 0 && isRefresh(event.detail.url)) {
    event.preventDefault()
    refreshBlocked = true
  }
}

document.addEventListener("fsp:increment", submissionStarted)
document.addEventListener("fsp:decrement", submissionEnded)
document.addEventListener("turbo:submit-start", submissionStarted)
document.addEventListener("turbo:submit-end", submissionEnded)
document.addEventListener("turbo:before-visit", guardRefresh)

// -- Morph Lock --

document.addEventListener("turbo:submit-start", (event) => {
  event.target.setAttribute("data-morphable", "")
})

document.addEventListener("turbo:before-morph-element", (event) => {
  const el = event.target
  const { newElement } = event.detail
  if (!newElement) return

  // Auto-detect client-rendered text: Stimulus-controlled leaf elements
  // (no child elements) where the server sends empty content. Copies text
  // to newElement so attributes can still morph while text is preserved.
  if (el.hasAttribute("data-controller") && el.children.length === 0 &&
      el.textContent && !newElement.textContent) {
    newElement.textContent = el.textContent
  }

  // Preserve client-set inline styles. If the old element has a style
  // attribute but the server-rendered new element doesn't, the styles
  // were set client-side (e.g., Stimulus controllers setting opacity).
  //
  // Never carry `display` across morph. Reactor (and similar) set
  // style.display = "none" on data-*-show nodes. After a strategy-switch
  // form reload, morph can map those nodes onto new fields (e.g. Worker
  // library) that have no show binding, leaving them permanently hidden.
  if (el.hasAttribute("style") && !newElement.hasAttribute("style")) {
    const cleaned = el.getAttribute("style")
      .split(";")
      .map((part) => part.trim())
      .filter((part) => part.length > 0 && !/^display\s*:/i.test(part))
      .join("; ")
    if (cleaned) newElement.setAttribute("style", cleaned)
  }

  const locked =
    el.tagName === "FORM" && el.querySelector("[name=commit], [name=button]") ||
    el.tagName === "TURBO-FRAME" && hasNavigatedSrc(el)

  const unlocked =
    el.hasAttribute("data-morphable") ||
    el.querySelector("[data-morphable]")

  if (locked && !unlocked) {
    event.preventDefault()
  }
})
